"use client";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ImageTile from "../../atoms/imageTile";
import Loader from "../../../components/atoms/loader";
import { DAYS_OF_WEEK, MONTH_NAMES } from "../../../constants/common/common";
import CustomImage from "../../atoms/customImage";

/**
 * Represents calendar for selecting date.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.onDateSelection prop for handling going forward when selecting entering delivery details.
 * @param {string} props.selectedDate - state value selectedDate date.
 * @param {boolean} props.isInternational - isInternational value.
 * @param {boolean} props.isPersonalizedProduct - is personlized product.
 * @param {boolean} props.isCourierProduct - is isCourierProduct.
 * @param {object} props.surgeDates - surgeDates.
 * @param {boolean} props.isMultiOrderProduct - is isMultiOrderProduct.
 * @param {array} props.multiOrderDateRange - multiOrderDateRange.
 * @returns {React.ReactElement} A React element displaying the calendar component.
 */
const Calendar = ({
  onDateSelection,
  selectedDate,
  isInternational = false,
  isPersonalizedProduct = false,
  isCourierProduct,
  surgeDates,
  isMultiOrderProduct,
  multiOrderDateRange,
  classes,
}) => {
  const deliveryDates = useSelector((state) => state.shippingDateAndSlotSelection?.deliveryDates?.deliveryDates);
  const internationalDeliveryDates = useSelector(
    (state) => state.shippingDateAndSlotSelection?.internationalDeliveryDates,
  );
  const blockedDates = useSelector((state) => state?.shippingDateAndSlotSelection?.blockedDates) || [];
  const loading = useSelector((state) => state.shippingDateAndSlotSelection.loading);

  const filterDateResponse = deliveryDates?.filter((item) => {
    return !blockedDates.deliveryBlockDates?.includes(item);
  });

  const deliveryDatesList = isInternational ? internationalDeliveryDates?.deliveryDates : filterDateResponse;

  const [day, month, year] = deliveryDatesList?.[0] ? deliveryDatesList[0].split("-").map(Number) : "";
  const startDate = day && month ? new Date(year, month - 1, day) : new Date();

  const [calendarDays, setCalendarDays] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(startDate);

  // eslint-disable-next-line no-undef
  const uniqueDeliveryDates = new Set(deliveryDatesList);
  const lastDatePresent = filterDateResponse?.[filterDateResponse?.length - 1];

  /**
   * This method is used to find if a day is blocked or not
   *
   * @param {object} day day object to get and manipulate on day operations.
   * @returns {boolean} returns true if day is blocked.
   */
  const isDayDisabled = (day) => !deliveryDatesList?.includes(day);

  /**
   * This function is for getting days in month.
   *
   * @function
   * @param {string} year selected year.
   * @param {string} month selected month.
   */
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

  /**
   * This function is for getting first day of the month.
   *
   * @function
   * @param {string} year selected year.
   * @param {string} month selected month.
   */
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  /**
   * Generates a calendar for a given month.
   * The calendar is represented as an array of weeks, where each week is an array of date strings in the format "DD-MM-YYYY".
   * Days before the first day of the month and after the last day of the month are filled with `null` to maintain a consistent 7-day week structure.
   *
   * @function
   * @param {Date} currentMonth - The current month for which the calendar is being generated.
   * @returns {Array<Array<string|null>>} A 2D array representing the calendar, where each sub-array is a week.
   */
  const generateCalendar = (currentMonth) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);

    const calendar = [];
    let week = Array(firstDayOfMonth).fill(null); // Pre-fill the first week with null until the first day of the month

    for (let day = 1; day <= daysInMonth; day++) {
      const day_ = String(day).padStart(2, "0");
      const month = String(currentMonth.getMonth() + 1).padStart(2, "0");
      const year = currentMonth?.getFullYear();

      week.push(`${day_}-${month}-${year}`); // Add current date to the week

      if (week?.length === 7) {
        calendar.push(week); // Push the completed week to the calendar
        week = []; // Reset for the next week
      }
    }

    if (week?.length > 0) {
      week = [...week, ...Array(7 - week.length).fill(null)];
      calendar.push(week); // Push the final week
    }

    return calendar;
  };

  useEffect(() => {
    setCalendarDays(generateCalendar(currentMonth));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This function checks if the current date is in the same month as today.
   *
   * @function
   * @returns {boolean} True if the current date is in the same month as today, false otherwise.
   */
  const isCurrentMonth = () => {
    return currentMonth.getFullYear() === startDate.getFullYear() && currentMonth.getMonth() === startDate.getMonth();
  };

  /**
   * This function is for handling the click of previous button.
   *
   * @function
   * @returns {void}
   */
  const onHandlePreviousMonth = () => {
    if (!isCurrentMonth()) {
      const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
      setCurrentMonth(newMonth);
      setCalendarDays(generateCalendar(newMonth));
    }
  };

  /**
   * Checks if the last date present corresponds to the current month and year.
   * Compares the month and year from `lastDatePresent` with the current month and year.
   *
   * @function
   * @returns {boolean} Returns `true` if the month and year from `lastDatePresent` match the current month and year, otherwise `false`.
   */
  const isLastMonthFound = () => {
    const islastMonth = lastDatePresent?.split("-");
    const value = islastMonth?.[1] + islastMonth?.[2];

    const month = String(currentMonth?.getMonth() + 1).padStart(2, "0");
    const year = currentMonth?.getFullYear();

    const value2 = month + year;
    return value === value2;
  };

  /**
   * This function is for handling the click of next button.
   *
   * @function
   * @returns {void}
   */
  const onHandleNextMonth = () => {
    if (!isLastMonthFound()) {
      const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
      setCurrentMonth(newMonth);
      setCalendarDays(generateCalendar(newMonth));
    }
  };

  /**
   * This function handles a date click event i.e. If the clicked date is not in the past,
   * updates the selected date and proceeds to the next delivery details step.
   *
   * @function
   * @param {number} day - The day of the month that was clicked.
   * @returns {void}
   */
  const onHandleDateClick = (day) => {
    if (uniqueDeliveryDates.has(day)) {
      onDateSelection(day);
    }
  };
  /**
   * Determines the appropriate CSS classes for a given day based on the selected date and availability.
   * If the day matches the selected date, it returns a style indicating that the date is selected.
   * If the day is available for delivery, it returns a style for an interactive day.
   * Otherwise, it returns a style indicating the date is unavailable.
   *
   * @function
   * @param {string} day - The current day in "DD-MM-YYYY" format.
   * @returns {string} A string containing the appropriate CSS classes for the given day.
   */
  const handleStyle = (day) => {
    if (selectedDate !== undefined && isMultiOrderProduct ? multiOrderDateRange?.includes(day) : selectedDate === day) {
      return "rounded-full cursor-pointer bg-green-200 text-white-100";
    }
    return uniqueDeliveryDates.has(day)
      ? "bg-white cursor-pointer hover:rounded-full hover:bg-green-200 hover:text-white-100"
      : "cursor-not-allowed text-gray-300 line-through";
  };

  return (
    <>
      <div
        className={`container mx-auto w-[100%] rounded-[20px] ${!loading ? "border-1" : ""} border-thinLightGrey ${classes}`}
        // eslint-disable-next-line react/jsx-handler-names
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <Loader />
        ) : (
          <>
            <div className="flex items-center justify-between border-b-1 border-thinLightGrey pb-12 pt-20">
              <ImageTile
                imgSrc={isCurrentMonth() ? "/icons/arrow-left-grey.svg" : "/icons/arrow-left-black.svg"}
                imgHeight={16}
                imgWidth={16}
                imgAlt="arrow-left"
                imgClass={`${isCurrentMonth() ? "" : "cursor-pointer"} ml-5`}
                onClick={onHandlePreviousMonth}
                disabled={isCurrentMonth()}
                isAdjustWidth={true}
              />
              <h2 className="text-base font-500">
                {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h2>
              <ImageTile
                imgSrc={"/icons/arrow-right-black.svg"}
                imgHeight={16}
                imgWidth={16}
                imgAlt="arrow-right"
                imgClass="cursor-pointer mr-5"
                disabled={isLastMonthFound()}
                onClick={onHandleNextMonth}
                isAdjustWidth={true}
              />
            </div>
            <div className="grid grid-cols-7 justify-items-center gap-12 px-20 pb-20 pt-12 text-xs font-medium not-italic text-grey-500">
              {DAYS_OF_WEEK?.map((day, index) => (
                <div key={index} className="text-center">
                  {day}
                </div>
              ))}
              {calendarDays?.map((week, weekIndex) => (
                <React.Fragment key={weekIndex}>
                  {week?.map((day, dayIndex) => {
                    const isSurge = surgeDates?.indexOf(day) !== -1;
                    const isDayBlocked = isDayDisabled(day);
                    return (
                      <div key={dayIndex}>
                        <div
                          onClick={() => onHandleDateClick(day)}
                          className={`flex h-32 w-32 items-center justify-center text-center text-14 font-600 text-fnp-500 md:text-16  ${handleStyle(day)}`}
                        >
                          {day?.split("-")?.[0]}
                        </div>
                        {isInternational && isSurge && !isDayBlocked && !isPersonalizedProduct && !isCourierProduct && (
                          <sup className="left-[20px] top-[-38px]">
                            <CustomImage
                              width={14}
                              height={14}
                              src="/icons/price-surge-icon.svg"
                              alt="price_surge_icon"
                            />
                          </sup>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};
Calendar.propTypes = {
  onDateSelection: PropTypes.func.isRequired,
  selectedDate: PropTypes.string,
  isInternational: PropTypes.bool,
  isPersonalizedProduct: PropTypes.bool,
  isCourierProduct: PropTypes.bool,
  surgeDates: PropTypes.array,
  isMultiOrderProduct: PropTypes.bool,
  multiOrderDateRange: PropTypes.array,
  classes: PropTypes.string,
};
export default Calendar;
